import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import {
  CheckCircle2,
  Clock3,
  Film,
  Info,
  Instagram,
  Loader2,
  ShieldCheck,
  Unplug,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { apiClient } from '@/lib/api/client'
import { useAuthStore } from '@/features/auth/stores/authStore'

type Account = { accountId: string; username: string; accountType: string; stories: boolean }
type Connection = Account & { id: string; status: string; expiresAt: string; revision: number }
type FilmAsset = {
  id: string
  title: string
  recipeSha256: string
  videos: { role: string; sha256: string }[]
}
type Notice = { text: string; kind: 'success' | 'error' }
const base = '/api/v1/film-social'
const typeLabel = (type: string) =>
  type === 'PROFESSIONAL_UNVERIFIED'
    ? 'Professional account'
    : type === 'BUSINESS'
      ? 'Business account'
      : 'Creator account'
const failureMessages: Record<string, string> = {
  permissions:
    'Meta did not grant all required permissions. Restart authorization and check Easel’s app permissions and selected assets.',
  eligible_accounts:
    'No eligible Page-linked professional Instagram account was found. Check the linked Facebook Page and your content-management access.',
  no_pages:
    'Meta returned no Facebook Pages for this grant. Reconnect, choose Edit settings in Meta, and share the Facebook Page linked to your Instagram account. If the Page is missing there, check your Page access in Meta Business Suite.',
  no_linked_instagram:
    'Meta returned Pages, but none exposed a linked professional Instagram account. Check that the intended Page is selected and linked to your Instagram account in Meta Business Suite.',
  no_content_access:
    'A linked Instagram account was found, but Meta did not return content-management rights on its Facebook Page. Check your Page content access in Meta Business Suite; business ownership alone is not this permission.',
  code_exchange:
    'Meta rejected the authorization exchange. Restart from this page; an administrator can check the callback and app configuration.',
  long_exchange:
    'Meta could not establish a long-lived connection. Restart authorization or contact an administrator.',
  token_identity:
    'The Meta grant could not be verified for Easel. Restart authorization or contact an administrator.',
  expiry: 'The Meta grant has no usable expiry. Restart authorization or contact an administrator.',
  pages:
    'Meta Page discovery failed. Check Easel’s permissions and your access to the linked Page.',
  profile:
    'Meta could not verify the linked Instagram profile. Check Page linkage and permissions.',
  capability:
    'Meta could not verify the account capability. Reconnect or contact an administrator.',
}
const pill = 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium'
const selectStyle =
  'min-h-11 w-full rounded-lg border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50'
function dateLabel(value: string, time = false) {
  const date = new Date(value)
  if (!Number.isFinite(date.getTime())) return 'Date unavailable'
  return time
    ? date.toLocaleString()
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}
function Steps() {
  return (
    <ol className="grid gap-3 sm:grid-cols-3">
      {[
        ['Authorize in Meta', 'Share only the intended Facebook Page and Instagram account.'],
        [
          'Save your account',
          'Back in Easel, select the account before the ten-minute timer ends.',
        ],
        ['Review & test a film', 'Approve a film, then run a read-only check. Nothing is posted.'],
      ].map(([title, detail], index) => (
        <li key={title} className="flex gap-3 rounded-xl border bg-background p-4">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
            {index + 1}
          </span>
          <div>
            <p className="text-sm font-medium">{title}</p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{detail}</p>
          </div>
        </li>
      ))}
    </ol>
  )
}
export function FilmSocialPanel() {
  const owner = useAuthStore((s) => s.user?.id)
  const qc = useQueryClient()
  const [params, setParams] = useSearchParams()
  const grant = params.get('grant') || ''
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<Notice | null>(null)
  const [connectOpen, setConnectOpen] = useState(false)
  const [disconnect, setDisconnect] = useState<Connection | null>(null)
  const [connectionId, setConnectionId] = useState('')
  const [filmId, setFilmId] = useState('')
  const [placement, setPlacement] = useState('REELS')
  const [testResult, setTestResult] = useState<string[] | null>(null)
  const [now, setNow] = useState(Date.now())
  // All credential-related caches stay scoped to the direct signed-in owner.
  const connections = useQuery({
    queryKey: ['film-social', owner],
    queryFn: async () =>
      (
        await apiClient.get<{
          connections: Connection[]
          businessDiscoveryPilot?: boolean
          publicationEnabled?: boolean
        }>(`${base}/connections`)
      ).data,
    retry: false,
  })
  const saved = connections.data?.connections || []
  const connected = saved.filter((c) => c.status === 'connected')
  const choices = useQuery({
    queryKey: ['film-social-grant', owner, grant],
    queryFn: async () =>
      (
        await apiClient.get<{ accounts: Account[]; expiresAt?: string }>(
          `${base}/grants/${encodeURIComponent(grant)}`
        )
      ).data,
    enabled: !!grant,
    retry: false,
  })
  const deadline = choices.data?.expiresAt ? Date.parse(choices.data.expiresAt) : undefined
  const selectionExpired = deadline !== undefined && (!Number.isFinite(deadline) || deadline <= now)
  useEffect(() => {
    if (!grant || deadline === undefined) return
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [grant, deadline])
  const films = useQuery({
    queryKey: ['film-social-films', owner],
    queryFn: async () => (await apiClient.get<{ jobs: FilmAsset[] }>(`${base}/films`)).data.jobs,
    enabled: connected.length > 0,
    retry: false,
  })
  const approvedFilms = films.data?.filter((f) => f.videos.length > 0) || []
  const destination = connected.find((c) => c.id === connectionId)
  const storyAllowed = destination?.accountType === 'BUSINESS'
  useEffect(() => {
    setTestResult(null)
  }, [connectionId, filmId, placement])
  useEffect(() => {
    if (!storyAllowed && placement === 'STORIES') setPlacement('REELS')
  }, [storyAllowed, placement])
  const errorText =
    params.get('social') === 'failed'
      ? failureMessages[params.get('reason') || ''] ||
        'Authorization did not complete. Please connect again.'
      : choices.error || selectionExpired
        ? 'Account selection expired or is unavailable. Check saved connections below; if your account is absent, connect Instagram again and select it within ten minutes.'
        : ''
  async function perform(fn: () => Promise<void>) {
    setBusy(true)
    setNotice(null)
    try {
      await fn()
      await qc.invalidateQueries({ queryKey: ['film-social', owner] })
    } catch (e) {
      const error = e as { response?: { data?: { error?: string; code?: string } } }
      if (error.response?.data?.code === 'SELECTION_UNAVAILABLE') {
        setParams({ view: 'accounts' }, { replace: true })
        qc.removeQueries({ queryKey: ['film-social-grant', owner, grant], exact: true })
      }
      setNotice({
        kind: 'error',
        text:
          error.response?.data?.error ||
          'Instagram connection unavailable. Restart authorization or contact an administrator.',
      })
    } finally {
      setBusy(false)
    }
  }
  function selectAccount(account: Account) {
    return perform(async () => {
      await apiClient.post(`${base}/grants/${encodeURIComponent(grant)}/select`, {
        accountId: account.accountId,
      })
      setParams({ view: 'accounts' }, { replace: true })
      qc.removeQueries({ queryKey: ['film-social-grant', owner, grant], exact: true })
      setNotice({ kind: 'success', text: `Connected @${account.username}. No posts were created.` })
    })
  }
  return (
    <section className="space-y-6 font-sans" aria-labelledby="instagram-heading">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border bg-card">
            <Instagram aria-hidden="true" className="h-6 w-6" />
          </div>
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-widest text-muted-foreground">
              Film Studio · destinations
            </p>
            <h2 id="instagram-heading" className="text-2xl font-semibold tracking-tight">
              Instagram connections
            </h2>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">
              Manage where your films can go. Connecting an account saves access; it does not
              approve or publish a film.
            </p>
          </div>
        </div>
        <Button
          className="min-h-11 shrink-0"
          disabled={busy || connections.isPending || !!connections.error}
          onClick={() => setConnectOpen(true)}
        >
          <Instagram aria-hidden="true" className="mr-2 h-4 w-4" />
          {connected.length ? 'Add or reconnect account' : 'Connect Instagram'}
        </Button>
      </header>
      {(!grant || choices.error || selectionExpired) && (
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border bg-card p-4">
            <p className="text-xs text-muted-foreground">Saved connections</p>
            <p className="mt-1 text-2xl font-semibold">
              {connections.isPending || connections.error ? '—' : connected.length}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {connected.length
                ? 'Access saved securely in Easel'
                : 'Connect and select an account'}
            </p>
          </div>
          <div className="rounded-xl border bg-card p-4">
            <p className="text-xs text-muted-foreground">Approved films for your account</p>
            <p className="mt-1 text-2xl font-semibold">
              {!connected.length || films.isPending || films.error ? '—' : approvedFilms.length}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Reviewed films available for a delivery check
            </p>
          </div>
          <div className="rounded-xl border bg-muted/40 p-4">
            <p className="text-xs text-muted-foreground">Instagram publishing</p>
            <p className="mt-1 text-xl font-semibold">
              {connections.data?.publicationEnabled ? 'Human-approved releases' : 'Not enabled'}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {connections.data?.publicationEnabled
                ? 'Use Scheduling for exact-film release approval'
                : 'Connection & read-only testing only'}
            </p>
          </div>
        </div>
      )}
      {(notice || errorText) && (
        <div
          role="status"
          className={`flex items-start gap-3 rounded-xl border p-4 text-sm leading-relaxed ${notice?.kind === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-amber-200 bg-amber-50 text-amber-950'}`}
        >
          <Info aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0" />
          <p>{notice?.text || errorText}</p>
        </div>
      )}
      {connections.error && (
        <div
          role="alert"
          className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950"
        >
          <p className="font-medium">Connection settings unavailable</p>
          <p className="mt-1">
            Instagram setup is not enabled or is unavailable. An administrator must configure the
            Easel app first.
          </p>
          <Button variant="outline" className="mt-3" onClick={() => void connections.refetch()}>
            Retry loading accounts
          </Button>
        </div>
      )}
      {grant && choices.isPending && (
        <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
          Loading the accounts you authorized…
        </p>
      )}
      {grant && choices.data && !choices.error && !selectionExpired && (
        <div className="space-y-4 rounded-2xl border-2 border-primary/30 bg-card p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Finish connecting · step 2 of 3
              </p>
              <h3 className="mt-2 text-lg font-semibold">Choose the account to connect</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Meta authorization succeeded. Select an account to save the connection.
              </p>
            </div>
            <span className={`${pill} bg-amber-100 text-amber-900`}>
              <Clock3 aria-hidden="true" className="h-3.5 w-3.5" />
              {deadline !== undefined
                ? `Selection expires in ${Math.max(0, Math.ceil((deadline - now) / 1000))} seconds.`
                : 'Save within ten minutes'}
            </span>
          </div>
          {choices.data.accounts.map((a) => (
            <button
              key={a.accountId}
              className="flex min-h-16 w-full flex-wrap items-center justify-between gap-3 rounded-xl border bg-background p-4 text-left transition hover:border-primary hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              disabled={busy}
              onClick={() => void selectAccount(a)}
            >
              <span className="min-w-0">
                <span className="block break-words text-base font-semibold">@{a.username}</span>
                <span className="mt-1 block text-xs text-muted-foreground">
                  {typeLabel(a.accountType)} ·{' '}
                  {a.stories
                    ? 'Stories account type verified'
                    : 'Stories eligibility needs verification'}
                </span>
              </span>
              <span className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground">
                {busy ? 'Saving…' : 'Save connection'}
              </span>
            </button>
          ))}
        </div>
      )}
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Your accounts</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Saved access and publishing eligibility are separate statuses.
          </p>
        </div>
        {connections.isPending && (
          <div
            className="rounded-xl border bg-card p-6 text-sm text-muted-foreground"
            role="status"
          >
            Loading saved accounts…
          </div>
        )}
        {!connections.isPending &&
          !connections.error &&
          saved.length === 0 &&
          !(grant && choices.data && !choices.error && !selectionExpired) && (
            <div className="space-y-5 rounded-2xl border bg-card p-5 sm:p-6">
              <div>
                <h4 className="font-semibold">No Instagram account saved yet</h4>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  Connect through Meta, then return here and select the destination. No passwords or
                  tokens need to be pasted into Easel.
                </p>
              </div>
              <Steps />
            </div>
          )}
        {saved.map((c) => (
          <article
            key={c.id}
            aria-label={`Instagram account @${c.username}`}
            className="overflow-hidden rounded-2xl border bg-card shadow-subtle"
          >
            <div className="flex flex-wrap items-start justify-between gap-4 p-5 sm:p-6">
              <div className="flex min-w-0 items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-muted">
                  <Instagram aria-hidden="true" className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <h4 className="break-words text-lg font-semibold">@{c.username}</h4>
                  <p className="mt-1 text-sm text-muted-foreground">{typeLabel(c.accountType)}</p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {c.status === 'connected'
                      ? `Reconnect by ${dateLabel(c.expiresAt)}`
                      : 'Reconnect to restore access'}
                  </p>
                </div>
              </div>
              <span
                className={`${pill} ${c.status === 'connected' ? 'bg-emerald-100 text-emerald-900' : 'bg-amber-100 text-amber-900'}`}
              >
                {c.status === 'connected' && (
                  <CheckCircle2 aria-hidden="true" className="h-3.5 w-3.5" />
                )}
                {c.status === 'connected'
                  ? 'Connection saved'
                  : c.status === 'expired'
                    ? 'Access expired'
                    : 'Disconnected'}
              </span>
            </div>
            <div className="grid gap-4 border-t bg-muted/20 px-5 py-4 sm:grid-cols-3 sm:px-6">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Reels
                </p>
                <p className="mt-1 text-sm font-medium">
                  {c.status === 'connected'
                    ? 'Available for delivery checks'
                    : 'Reconnect required'}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Stories
                </p>
                <p className="mt-1 text-sm font-medium">
                  {c.status !== 'connected'
                    ? 'Reconnect required'
                    : c.accountType === 'BUSINESS'
                      ? 'Account type verified'
                      : c.accountType === 'CREATOR'
                        ? 'Not eligible in this connection flow'
                        : 'Needs account-type verification'}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Posting
                </p>
                <p className="mt-1 text-sm font-medium">
                  {connections.data?.publicationEnabled
                    ? 'Separate release approval required'
                    : 'Paused'}
                </p>
              </div>
            </div>
            {c.status === 'connected' && c.accountType === 'PROFESSIONAL_UNVERIFIED' && (
              <div className="flex gap-2 border-t px-5 py-4 text-sm leading-relaxed sm:px-6">
                <Info
                  aria-hidden="true"
                  className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                />
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">Your connection is saved.</span>{' '}
                  Meta did not confirm Business vs Creator account type through this API. Only
                  Stories are held for this verification; you do not need to reconnect to fix this
                  label.
                </p>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t px-5 py-3 sm:px-6">
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer py-2">Connection details</summary>
                <p className="py-2">
                  Reauthorize by {dateLabel(c.expiresAt, true)} · connection revision {c.revision}.
                  Credentials are encrypted on the server.
                </p>
              </details>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy || c.status === 'disconnected'}
                onClick={() => setDisconnect(c)}
              >
                <Unplug aria-hidden="true" className="mr-2 h-4 w-4" />
                Disconnect
              </Button>
            </div>
          </article>
        ))}
      </div>
      <details className="rounded-2xl border bg-card">
        <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-3 p-5 sm:p-6">
          <span className="flex items-center gap-3">
            <Film aria-hidden="true" className="h-5 w-5 text-muted-foreground" />
            <span>
              <span className="block font-semibold">Test film delivery · no posting</span>
              <span className="mt-1 block text-xs text-muted-foreground">
                Check an approved film against a saved destination
              </span>
            </span>
          </span>
          <span className={`${pill} bg-muted text-muted-foreground`}>Read-only · expand</span>
        </summary>
        <div className="space-y-5 border-t p-5 sm:p-6">
          <p className="text-sm leading-relaxed text-muted-foreground">
            This verifies the exact approved film and your account access. It does not upload media,
            schedule a release or post to Instagram.
          </p>
          {!connected.length ? (
            <p className="rounded-xl bg-muted p-4 text-sm">
              Save an Instagram connection first to enable delivery checks.
            </p>
          ) : films.isPending ? (
            <p role="status" className="text-sm text-muted-foreground">
              Loading your approved films…
            </p>
          ) : films.error ? (
            <div className="space-y-3">
              <p className="text-sm">
                Approved films could not be loaded. Try again before testing delivery.
              </p>
              <Button variant="outline" onClick={() => void films.refetch()}>
                Retry loading films
              </Button>
            </div>
          ) : !approvedFilms.length ? (
            <div className="rounded-xl border bg-muted/30 p-4">
              <h4 className="font-medium">Next step: review a film</h4>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                No approved films are available for your signed-in account yet. A draft must pass
                review before it can be tested here.
              </p>
              <Button asChild variant="outline" className="mt-3">
                <Link to="/app/films?view=library">Open Film Studio library</Link>
              </Button>
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="instagram-destination" className="mb-2 block text-sm font-medium">
                    Instagram destination
                  </label>
                  <select
                    id="instagram-destination"
                    aria-label="Instagram destination"
                    className={selectStyle}
                    value={connectionId}
                    onChange={(e) => setConnectionId(e.target.value)}
                    disabled={busy}
                  >
                    <option value="">Choose your account</option>
                    {connected.map((c) => (
                      <option key={c.id} value={c.id}>
                        @{c.username}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="approved-delivery-film"
                    className="mb-2 block text-sm font-medium"
                  >
                    Approved film
                  </label>
                  <select
                    id="approved-delivery-film"
                    aria-label="Approved film for delivery test"
                    className={selectStyle}
                    value={filmId}
                    onChange={(e) => setFilmId(e.target.value)}
                    disabled={busy}
                  >
                    <option value="">Choose your approved film</option>
                    {approvedFilms.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-end">
                <div className="w-full sm:w-48">
                  <label htmlFor="instagram-placement" className="mb-2 block text-sm font-medium">
                    Format to check
                  </label>
                  <select
                    id="instagram-placement"
                    aria-label="Instagram placement"
                    className={selectStyle}
                    value={placement}
                    onChange={(e) => setPlacement(e.target.value)}
                    disabled={busy}
                  >
                    <option value="REELS">Reel</option>
                    <option value="STORIES" disabled={!storyAllowed}>
                      Story{storyAllowed ? '' : ' · unavailable'}
                    </option>
                  </select>
                </div>
                <Button
                  className="min-h-11"
                  disabled={
                    busy || !destination || !filmId || (placement === 'STORIES' && !storyAllowed)
                  }
                  onClick={() =>
                    void perform(async () => {
                      const film = approvedFilms.find((f) => f.id === filmId)
                      if (!film) return
                      setTestResult(null)
                      const { data } = await apiClient.post<{ blockers: string[] }>(
                        `${base}/preflight`,
                        {
                          connectionId,
                          jobId: film.id,
                          recipeSha256: film.recipeSha256,
                          artifactSha256: film.videos[0].sha256,
                          placement,
                        }
                      )
                      setTestResult(data.blockers)
                    })
                  }
                >
                  {busy && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}Run
                  delivery preflight
                </Button>
              </div>
              {destination && !storyAllowed && (
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Stories require a verified Business account in this connection flow. You can still
                  check a Reel.
                </p>
              )}
              {testResult && (
                <div role="status" className="space-y-2 rounded-xl border bg-muted/30 p-4">
                  <p className="flex items-center gap-2 text-sm font-medium">
                    <CheckCircle2 aria-hidden="true" className="h-4 w-4 text-emerald-700" />
                    Read-only checks passed. Zero posts created.
                  </p>
                  {testResult.map((blocker, i) => (
                    <p key={i} className="text-sm leading-relaxed text-muted-foreground">
                      {blocker}
                    </p>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </details>
      <details className="rounded-xl border bg-muted/20 px-5 py-4 text-sm">
        <summary className="flex cursor-pointer items-center gap-2 font-medium">
          <ShieldCheck aria-hidden="true" className="h-4 w-4" />
          Connection setup & permissions
        </summary>
        <div className="mt-4 space-y-4 text-sm leading-relaxed text-muted-foreground">
          <Steps />
          <p>
            Already connected Easel to Facebook? In Meta, choose{' '}
            <strong className="text-foreground">Edit settings</strong> to review the Instagram
            accounts and linked Pages you share. The final account selection happens here in Easel.
          </p>
          {connections.data?.businessDiscoveryPilot && (
            <p className="rounded-lg border bg-background p-3">
              Business-discovery pilot: this connection also requests{' '}
              <code>business_management</code>. Meta grants broader Business Manager read/write
              access; Easel uses it only for read-only discovery here. Select only the intended
              business, Page and Instagram account. No posts or business changes are made.
            </p>
          )}
          <p>
            Only the selected Page credential is stored, encrypted on the server. Connecting does
            not approve a film. Publishing requires a separate exact-film release approval and a
            delivery queue. Its current availability is shown in Schedules.
          </p>
        </div>
      </details>
      <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
        <DialogContent className="max-h-[90dvh] max-w-2xl overflow-y-auto font-sans">
          <DialogHeader>
            <DialogTitle>Connect Instagram with Easel</DialogTitle>
            <DialogDescription>
              You’ll leave Easel for Meta, then return here to save your account. No posts are
              created.
            </DialogDescription>
          </DialogHeader>
          <Steps />
          {connections.data?.businessDiscoveryPilot && (
            <p className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-950">
              This pilot requests <code>business_management</code>, which grants Meta Business
              Manager read/write access. Easel uses it only for read-only discovery. Select only the
              intended business, Facebook Page and Instagram account.
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            Authorization and account selection each have a ten-minute window. Keep the same browser
            tab and save your account as soon as you return.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={busy} onClick={() => setConnectOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={busy || !!connections.error}
              onClick={() =>
                void perform(async () => {
                  const { data } = await apiClient.post<{ url: string }>(`${base}/oauth/start`)
                  window.location.assign(data.url)
                })
              }
            >
              {busy && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}Continue
              to Meta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!disconnect}
        onOpenChange={(open) => {
          if (!open) setDisconnect(null)
        }}
      >
        <DialogContent className="font-sans">
          <DialogHeader>
            <DialogTitle>Disconnect @{disconnect?.username}?</DialogTitle>
            <DialogDescription>
              This removes the saved credential from Easel and stops delivery checks for this
              connection. It does not remove posts or revoke Easel in Meta’s settings.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" disabled={busy} onClick={() => setDisconnect(null)}>
              Keep connected
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() =>
                void perform(async () => {
                  if (!disconnect) return
                  await apiClient.delete(`${base}/connections/${disconnect.id}`)
                  setDisconnect(null)
                  setConnectionId('')
                  setTestResult(null)
                  setNotice({
                    kind: 'success',
                    text: 'Disconnected in Easel; the saved credential was removed.',
                  })
                })
              }
            >
              Disconnect account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
